import React, { useEffect, useMemo, useState } from "react";

import { Navigate } from "react-router-dom";

import { observer } from "mobx-react-lite";

import Loader from "../components/Loader";

import ApiData from "../api/ApiData";

import User from "../api/User";

import RolesState from "../api/RolesState";

import ReportData from "../api/ReportData";

import { ContainerSubTitleGrey } from "../components/ContainerSubTitleGrey";

import { DashboardEntityCard } from "../components/myCompanies/DashboardEntityCard";
import { MyCompaniesFilters } from "../components/myCompanies/MyCompaniesFilters";
import {
  applyMyCompaniesFilters,
  DEFAULT_MY_COMPANIES_FILTERS,
} from "../utils/myCompaniesFilterUtils";

import {
  DASHBOARD_SUBSCRIPTION_MAX_PERIOD,
  enrichDashboardCompaniesSubscription,
  listMyDashboardEntities,
} from "../utils/myCompaniesDashboardUtils";
import {
  buildCompanyTotalsMap,
  canViewEventTotals,
  getRolling30DaysRangeToToday,
  totalsToAvgPerDayInteger,
} from "../utils/eventTotalsSaUtils";

const MyCompanies = observer(() => {
  const [dashboardCompanies, setDashboardCompanies] = useState(null);
  const [eventTotalsByCompanyId, setEventTotalsByCompanyId] = useState(null);
  const [eventTotalsLoading, setEventTotalsLoading] = useState(false);
  const [eventTotalsError, setEventTotalsError] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_MY_COMPANIES_FILTERS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (ApiData.myDashboardCompanies === null) {
        await ApiData.loadMyDashboardCompanies();
      }
      if (!cancelled) {
        const list = ApiData.myDashboardCompanies;
        setDashboardCompanies(Array.isArray(list) ? [...list] : []);
      }

      await ReportData.ensureVersionReportSources();
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (dashboardCompanies === null) {
      return;
    }

    const base = listMyDashboardEntities(dashboardCompanies, User.user?.id);

    const needsFetch = base.some(
      (c) => c.subscription_rel?.[0]?.validity_date == null,
    );

    if (!needsFetch) {
      return;
    }

    if (
      !RolesState.get_subscription_id &&
      !User.user?.is_superuser &&
      !RolesState.subscription_rights
    ) {
      return;
    }

    ApiData.allReportSubscription(DASHBOARD_SUBSCRIPTION_MAX_PERIOD);
  }, [dashboardCompanies, User.user?.id]);

  const canShowLicensedServers =
    RolesState.get_all_licensing === true || User.user?.is_superuser === true;

  const entities = useMemo(() => {
    if (dashboardCompanies === null) {
      return [];
    }

    const base = listMyDashboardEntities(dashboardCompanies, User.user?.id);

    const innSet = new Set(base.map((c) => String(c.inn)));

    return enrichDashboardCompaniesSubscription(
      base,

      ApiData.subscription,

      innSet,
    );
  }, [dashboardCompanies, ApiData.subscription, User.user?.id]);

  const canViewEventTotalsBlock = canViewEventTotals(RolesState, User.user);

  const serversPending =
    canShowLicensedServers && !ReportData.versionReportSourcesReady;

  const filteredEntities = useMemo(() => {
    return applyMyCompaniesFilters(entities, filters, {
      canShowLicensedServers,
      serversPending,
      softAllData: ApiData.soft_all_data,
      licensingAll: ApiData.licensing_all,
      eventTotalsByCompanyId,
      eventTotalsLoading,
    });
  }, [
    entities,
    filters,
    canShowLicensedServers,
    serversPending,
    ApiData.soft_all_data,
    ApiData.licensing_all,
    eventTotalsByCompanyId,
    eventTotalsLoading,
  ]);

  useEffect(() => {
    if (!canViewEventTotalsBlock || entities.length === 0) {
      setEventTotalsByCompanyId(new Map());
      setEventTotalsLoading(false);
      return;
    }
    const ids = entities.map((c) => c.id).filter((id) => id != null);
    if (ids.length === 0) {
      setEventTotalsByCompanyId(new Map());
      return;
    }
    let cancelled = false;
    const { from, to } = getRolling30DaysRangeToToday();
    setEventTotalsLoading(true);
    setEventTotalsError(false);
    (async () => {
      const res = await ApiData.fetchEventTotalsSa({
        filterdate: [from, to],
        companyIds: ids,
      });
      if (cancelled) {
        return;
      }
      if (!res) {
        setEventTotalsError(true);
        setEventTotalsByCompanyId(new Map());
      } else {
        const raw = buildCompanyTotalsMap(res.companies);
        const bundleMap = new Map();
        for (const [id, totals] of raw.entries()) {
          bundleMap.set(id, {
            month: totals,
            avg: totalsToAvgPerDayInteger(totals, from, to),
          });
        }
        setEventTotalsByCompanyId(bundleMap);
      }
      setEventTotalsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewEventTotalsBlock, entities]);

  if (!RolesState.companys && !RolesState.company_partners) {
    return <Navigate to="/noright" />;
  }

  return (
    <div className="main-page company-card-v4 my-companies-page-v4">
      {dashboardCompanies !== null ? (
        <>
          <header className="card header-card company-list-page-header">
            <div className="header-top">
              <div className="company-title">
                <div className="compamy-title-name-container">
                  <h1>Мои компании</h1>
                </div>
              </div>
            </div>
          </header>

          {entities.length === 0 ? (
            <ContainerSubTitleGrey text="Нет назначенных компаний и партнёров" />
          ) : (
            <>
              <MyCompaniesFilters
                filters={filters}
                onChange={setFilters}
                canViewEventTotals={canViewEventTotalsBlock}
              />
              {filteredEntities.length === 0 ? (
                <ContainerSubTitleGrey text="Нет карточек по выбранным фильтрам" />
              ) : (
                <div className="my-companies-grid">
                  {filteredEntities.map((company) => (
                    <DashboardEntityCard
                      key={company.inn}
                      company={company}
                      canShowLicensedServers={canShowLicensedServers}
                      canViewEventTotals={canViewEventTotalsBlock}
                      eventTotalsMonth={
                        eventTotalsByCompanyId?.get(Number(company.id))?.month
                      }
                      eventTotalsAvg={
                        eventTotalsByCompanyId?.get(Number(company.id))?.avg
                      }
                      eventTotalsLoading={eventTotalsLoading}
                      eventTotalsError={eventTotalsError}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
});

export { MyCompanies };
